import { lazy, Suspense } from 'react'
import { Spinner } from '@components/primitives/Spinner.js'
import { FerramentaToolProvider } from './FerramentaToolContext.js'
import { GUIA_LABELS } from '@utils/guiaLabels.js'

export const FERRAMENTA_IDS = new Set<string>([
  'fin-03',
  'fin-04',
  'fin-05',
  'fin-06',
  'fin-07',
  'fin-08',
  'fin-09',
  'fin-10',
  'fin-11',
  'fin-13',
  'trib-02',
  'trib-04',
  'trib-06',
  'trib-07',
  'trib-08',
  'trib-09',
  'trib-10',
  'trib-12',
  'trib-14',
  'trib-18',
  'trib-22',
  'sc-02',
  'sc-03',
  'sc-04',
  'sc-05',
  'sc-06',
  'sc-07',
  'sc-08',
  'sc-09',
  'sc-10',
  'terreno-01',
  'terreno-02',
  'terreno-05',
  'terreno-06',
  'terreno-08',
  'terreno-10',
  'terreno-11',
  'terreno-13',
  'terreno-17',
  'terreno-20',
  'terreno-21',
  'exe-06',
  'exe-05',
  'exe-10',
  'exe-04',
  'exe-01',
  'mdo-03',
  'mdo-04',
  'mdo-05',
  'mdo-06',
  'mdo-07',
  'mdo-09',
  'mdo-10',
  'proj-13',
  'proj-05',
  'proj-07',
  'proj-09',
  'proj-02',
  'proj-06',
  'proj-08',
  'proj-11',
])

const FichaTradicional = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='tradicional' />,
  })),
)
const FichaMetalica = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='metalica' />,
  })),
)
const FichaSteelFrame = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='steel-frame' />,
  })),
)
const FichaAlvenariaEstrutural = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='alvenaria-estrutural' />,
  })),
)
const FichaICF = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='icf' />,
  })),
)
const FichaTijoloEcologico = lazy(() =>
  import('./sistemas/FichaTecnicaSistema.js').then((m) => ({
    default: () => <m.FichaTecnicaSistema sistemaId='tijolo-ecologico' />,
  })),
)

const ContratoTotal = lazy(() =>
  import('./mao-de-obra/ComparadorContratosDetalhado.js').then((m) => ({
    default: () => <m.ComparadorContratosDetalhado contratoDestaque='total' />,
  })),
)
const ContratoParcial = lazy(() =>
  import('./mao-de-obra/ComparadorContratosDetalhado.js').then((m) => ({
    default: () => <m.ComparadorContratosDetalhado contratoDestaque='parcial' />,
  })),
)
const ContratoAdmin = lazy(() =>
  import('./mao-de-obra/ComparadorContratosDetalhado.js').then((m) => ({
    default: () => <m.ComparadorContratosDetalhado contratoDestaque='administracao' />,
  })),
)

const INSSContratacaoTotal = lazy(() =>
  import('./tributos/SimuladorINSSContratacao.js').then((m) => ({
    default: () => <m.SimuladorINSSContratacao modo='empreitada-total' />,
  })),
)
const INSSContratacaoParcial = lazy(() =>
  import('./tributos/SimuladorINSSContratacao.js').then((m) => ({
    default: () => <m.SimuladorINSSContratacao modo='empreitada-parcial' />,
  })),
)
const INSSContratacaoAdmin = lazy(() =>
  import('./tributos/SimuladorINSSContratacao.js').then((m) => ({
    default: () => <m.SimuladorINSSContratacao modo='administracao' />,
  })),
)
const INSSContratacaoDiarista = lazy(() =>
  import('./tributos/SimuladorINSSContratacao.js').then((m) => ({
    default: () => <m.SimuladorINSSContratacao modo='diarista' />,
  })),
)

const FERRAMENTA_MAP: Record<string, React.ComponentType> = {
  // Planejamento Financeiro (RFC-08D)
  'fin-03': lazy(() =>
    import('./financeiro/DivisorFases.js').then((m) => ({ default: m.DivisorFases })),
  ),
  'fin-04': lazy(() =>
    import('./financeiro/DivisorInsumos.js').then((m) => ({ default: m.DivisorInsumos })),
  ),
  'fin-05': lazy(() =>
    import('./financeiro/TabelaInsumos.js').then((m) => ({ default: m.TabelaInsumos })),
  ),
  'fin-06': lazy(() =>
    import('./financeiro/VerificadorTerreno.js').then((m) => ({ default: m.VerificadorTerreno })),
  ),
  'fin-07': lazy(() =>
    import('./financeiro/VerificadorEstrutura.js').then((m) => ({
      default: m.VerificadorEstrutura,
    })),
  ),
  'fin-08': lazy(() =>
    import('./financeiro/VerificadorEsquadrias.js').then((m) => ({
      default: m.VerificadorEsquadrias,
    })),
  ),
  'fin-09': lazy(() =>
    import('./financeiro/VerificadorRevestimentos.js').then((m) => ({
      default: m.VerificadorRevestimentos,
    })),
  ),
  'fin-10': lazy(() =>
    import('./financeiro/VerificadorDemaisItens.js').then((m) => ({
      default: m.VerificadorDemaisItens,
    })),
  ),
  'fin-11': lazy(() =>
    import('./financeiro/SimuladorViabilidade.js').then((m) => ({
      default: m.SimuladorViabilidade,
    })),
  ),
  'fin-13': lazy(() =>
    import('./financeiro/CalculadoraFinanciamento.js').then((m) => ({
      default: m.CalculadoraFinanciamento,
    })),
  ),
  // Tributos (RFC-08F)
  'trib-02': lazy(() =>
    import('./tributos/EsteiraRegularizacao.js').then((m) => ({ default: m.EsteiraRegularizacao })),
  ),
  'trib-04': lazy(() => import('./tributos/AlertaCNO.js').then((m) => ({ default: m.AlertaCNO }))),
  'trib-06': lazy(() =>
    import('./tributos/CalculadoraINSS.js').then((m) => ({ default: m.CalculadoraINSS })),
  ),
  'trib-07': INSSContratacaoTotal,
  'trib-08': INSSContratacaoParcial,
  'trib-09': INSSContratacaoAdmin,
  'trib-10': INSSContratacaoDiarista,
  'trib-12': lazy(() =>
    import('./tributos/SimuladorIR.js').then((m) => ({ default: m.SimuladorIR })),
  ),
  'trib-14': lazy(() =>
    import('./tributos/ChecklistDocumentosProtetores.js').then((m) => ({
      default: m.ChecklistDocumentosProtetores,
    })),
  ),
  'trib-18': lazy(() =>
    import('./tributos/CalculadoraFatorAjuste.js').then((m) => ({
      default: m.CalculadoraFatorAjuste,
    })),
  ),
  'trib-22': lazy(() =>
    import('./tributos/ChecklistSaudeFiscal.js').then((m) => ({ default: m.ChecklistSaudeFiscal })),
  ),
  // Sistemas Construtivos (RFC-08B)
  'sc-02': lazy(() =>
    import('./sistemas/SeletorSistema.js').then((m) => ({ default: m.SeletorSistema })),
  ),
  'sc-03': FichaTradicional,
  'sc-04': FichaMetalica,
  'sc-05': FichaSteelFrame,
  'sc-06': FichaAlvenariaEstrutural,
  'sc-07': FichaICF,
  'sc-08': FichaTijoloEcologico,
  'sc-09': lazy(() =>
    import('./sistemas/ComparadorSistemas.js').then((m) => ({ default: m.ComparadorSistemas })),
  ),
  'sc-10': lazy(() =>
    import('./sistemas/CalculadoraCustoSistema.js').then((m) => ({
      default: m.CalculadoraCustoSistema,
    })),
  ),
  // Terreno (RFC-08A)
  'terreno-01': lazy(() =>
    import('./terreno/DiagnosticoTopografia.js').then((m) => ({
      default: m.DiagnosticoTopografia,
    })),
  ),
  'terreno-02': lazy(() =>
    import('./terreno/CalculadoraCorteAterro.js').then((m) => ({
      default: m.CalculadoraCorteAterro,
    })),
  ),
  'terreno-05': lazy(() =>
    import('./terreno/VisualizadorEstrategias.js').then((m) => ({
      default: m.VisualizadorEstrategias,
    })),
  ),
  'terreno-06': lazy(() =>
    import('./terreno/ComparadorEscalonamento.js').then((m) => ({
      default: m.ComparadorEscalonamento,
    })),
  ),
  'terreno-08': lazy(() =>
    import('./terreno/SimuladorCota.js').then((m) => ({ default: m.SimuladorCota })),
  ),
  'terreno-10': lazy(() =>
    import('./terreno/MontadorMetragem.js').then((m) => ({ default: m.MontadorMetragem })),
  ),
  'terreno-11': lazy(() =>
    import('./terreno/CalculadoraCaixaoPerdido.js').then((m) => ({
      default: m.CalculadoraCaixaoPerdido,
    })),
  ),
  'terreno-13': lazy(() =>
    import('./terreno/CanvasZoneamento.js').then((m) => ({ default: m.CanvasZoneamento })),
  ),
  'terreno-17': lazy(() =>
    import('./terreno/ComparadorDeckLaje.js').then((m) => ({ default: m.ComparadorDeckLaje })),
  ),
  'terreno-20': lazy(() =>
    import('./terreno/DiagnosticoSPT.js').then((m) => ({ default: m.DiagnosticoSPT })),
  ),
  'terreno-21': lazy(() =>
    import('./terreno/SeletorFundacao.js').then((m) => ({ default: m.SeletorFundacao })),
  ),
  // Execução (RFC-08G)
  'exe-06': lazy(() =>
    import('./execucao/TimelineObra.js').then((m) => ({ default: m.TimelineObra })),
  ),
  'exe-05': lazy(() =>
    import('./execucao/ChecklistInicioObra.js').then((m) => ({ default: m.ChecklistInicioObra })),
  ),
  'exe-10': lazy(() =>
    import('./execucao/DecisorCobertura.js').then((m) => ({ default: m.DecisorCobertura })),
  ),
  'exe-04': lazy(() =>
    import('./execucao/IdentificadorRetrabalho.js').then((m) => ({
      default: m.IdentificadorRetrabalho,
    })),
  ),
  'exe-01': lazy(() =>
    import('./execucao/CronogramaDecisoes.js').then((m) => ({ default: m.CronogramaDecisoes })),
  ),
  // Mão de Obra (RFC-08E)
  'mdo-03': lazy(() =>
    import('./mao-de-obra/ComparadorContratacao.js').then((m) => ({
      default: m.ComparadorContratacao,
    })),
  ),
  'mdo-04': ContratoTotal,
  'mdo-05': ContratoParcial,
  'mdo-06': ContratoAdmin,
  'mdo-07': lazy(() =>
    import('./mao-de-obra/ChecklistContratoPeigoso.js').then((m) => ({
      default: m.ChecklistContratoPeigoso,
    })),
  ),
  'mdo-09': lazy(() =>
    import('./mao-de-obra/SimuladorFluxoPagamentos.js').then((m) => ({
      default: m.SimuladorFluxoPagamentos,
    })),
  ),
  'mdo-10': lazy(() =>
    import('./mao-de-obra/CalculadoraImpostoMDO.js').then((m) => ({
      default: m.CalculadoraImpostoMDO,
    })),
  ),
  // Projetos (RFC-08C)
  'proj-13': lazy(() =>
    import('./projetos/CalculadoraFatorCasca.js').then((m) => ({
      default: m.CalculadoraFatorCasca,
    })),
  ),
  'proj-05': lazy(() =>
    import('./projetos/AnalisadorCustoM2.js').then((m) => ({ default: m.AnalisadorCustoM2 })),
  ),
  'proj-07': lazy(() =>
    import('./projetos/IdentificadorAreasCara.js').then((m) => ({
      default: m.IdentificadorAreasCara,
    })),
  ),
  'proj-09': lazy(() =>
    import('./projetos/SimuladorVaos.js').then((m) => ({ default: m.SimuladorVaos })),
  ),
  'proj-02': lazy(() =>
    import('./projetos/ChecklistSequenciaProjetos.js').then((m) => ({
      default: m.ChecklistSequenciaProjetos,
    })),
  ),
  'proj-06': lazy(() =>
    import('./projetos/CalculadoraAreaAcabamento.js').then((m) => ({
      default: m.CalculadoraAreaAcabamento,
    })),
  ),
  'proj-08': lazy(() =>
    import('./projetos/CalculadoraAreaEquivalente.js').then((m) => ({
      default: m.CalculadoraAreaEquivalente,
    })),
  ),
  'proj-11': lazy(() =>
    import('./projetos/AnalisadorConsumoAco.js').then((m) => ({ default: m.AnalisadorConsumoAco })),
  ),
  // Catálogo RF-08 (/calculadoras) — ids do índice
  topografia: lazy(() =>
    import('./terreno/DiagnosticoTopografia.js').then((m) => ({
      default: m.DiagnosticoTopografia,
    })),
  ),
  'corte-aterro': lazy(() =>
    import('./terreno/CalculadoraCorteAterro.js').then((m) => ({
      default: m.CalculadoraCorteAterro,
    })),
  ),
  insolacao: lazy(() =>
    import('./terreno/AnalisadorInsolacao.js').then((m) => ({ default: m.AnalisadorInsolacao })),
  ),
  sondagem: lazy(() =>
    import('./terreno/CalculadoraSondagem.js').then((m) => ({ default: m.CalculadoraSondagem })),
  ),
  sistemas: lazy(() =>
    import('./sistemas/ComparadorSistemas.js').then((m) => ({ default: m.ComparadorSistemas })),
  ),
  espessura: lazy(() =>
    import('./sistemas/CalculadoraEspessura.js').then((m) => ({ default: m.CalculadoraEspessura })),
  ),
  vaos: lazy(() =>
    import('./projetos/SimuladorVaos.js').then((m) => ({ default: m.SimuladorVaos })),
  ),
  casca: lazy(() =>
    import('./projetos/CalculadoraFatorCasca.js').then((m) => ({
      default: m.CalculadoraFatorCasca,
    })),
  ),
  comodo: lazy(() =>
    import('./projetos/AnalisadorCustoM2.js').then((m) => ({ default: m.AnalisadorCustoM2 })),
  ),
  otimizador: lazy(() =>
    import('./projetos/OtimizadorAreas.js').then((m) => ({ default: m.OtimizadorAreas })),
  ),
  aberturas: lazy(() =>
    import('./projetos/CalculadoraAberturas.js').then((m) => ({ default: m.CalculadoraAberturas })),
  ),
  'pe-direito': lazy(() =>
    import('./projetos/CalculadoraPeDireito.js').then((m) => ({ default: m.CalculadoraPeDireito })),
  ),
  viabilidade: lazy(() =>
    import('./financeiro/SimuladorViabilidade.js').then((m) => ({
      default: m.SimuladorViabilidade,
    })),
  ),
  'curva-s': lazy(() =>
    import('./financeiro/CurvaDesembolso.js').then((m) => ({ default: m.CurvaDesembolso })),
  ),
  reserva: lazy(() =>
    import('./financeiro/CalculadoraReserva.js').then((m) => ({ default: m.CalculadoraReserva })),
  ),
  financiamento: lazy(() =>
    import('./financeiro/CalculadoraFinanciamento.js').then((m) => ({
      default: m.CalculadoraFinanciamento,
    })),
  ),
  inflacao: lazy(() =>
    import('./financeiro/InflacaoObra.js').then((m) => ({ default: m.InflacaoObra })),
  ),
  contratacao: lazy(() =>
    import('./mao-de-obra/TiposContratacao.js').then((m) => ({ default: m.TiposContratacao })),
  ),
  equipe: lazy(() =>
    import('./mao-de-obra/EquipeIdeal.js').then((m) => ({ default: m.EquipeIdeal })),
  ),
  pedreiro: lazy(() =>
    import('./mao-de-obra/CustoPedreiro.js').then((m) => ({ default: m.CustoPedreiro })),
  ),
  aditivos: lazy(() =>
    import('./mao-de-obra/DetectorAditivos.js').then((m) => ({ default: m.DetectorAditivos })),
  ),
  impostos: lazy(() =>
    import('./tributos/ImpostosTotais.js').then((m) => ({ default: m.ImpostosTotais })),
  ),
  inss: lazy(() =>
    import('./tributos/CalculadoraINSS.js').then((m) => ({ default: m.CalculadoraINSS })),
  ),
  cno: lazy(() =>
    import('./tributos/AlertaCNOCalc.js').then((m) => ({ default: m.AlertaCNOCalc })),
  ),
  habitese: lazy(() =>
    import('./tributos/HabiteSeChecklist.js').then((m) => ({ default: m.HabiteSeChecklist })),
  ),
  cronograma: lazy(() =>
    import('./execucao/CronogramaFisicoFinanceiro.js').then((m) => ({
      default: m.CronogramaFisicoFinanceiro,
    })),
  ),
  'anti-retrabalho': lazy(() =>
    import('./execucao/IdentificadorRetrabalho.js').then((m) => ({
      default: m.IdentificadorRetrabalho,
    })),
  ),
  atrasos: lazy(() =>
    import('./execucao/DetectorAtrasos.js').then((m) => ({ default: m.DetectorAtrasos })),
  ),
  'manta-telhado': lazy(() =>
    import('./execucao/MantaVsTelhado.js').then((m) => ({ default: m.MantaVsTelhado })),
  ),
}

interface Props {
  topicoId: string
  persistKey?: string
}

export function FerramentaRouter({ topicoId, persistKey }: Props) {
  const Ferramenta = FERRAMENTA_MAP[topicoId]
  const toolId = persistKey ?? topicoId

  if (!Ferramenta) {
    return (
      <p className='cf3-topico-placeholder__text'>
        Use o checklist desta etapa para aplicar à sua obra. Se precisar de números, abra o recurso
        pela etapa ou pelo menu {GUIA_LABELS.ferramentasIndex}.
      </p>
    )
  }

  return (
    <FerramentaToolProvider toolId={toolId}>
      <Suspense fallback={<Spinner />}>{<Ferramenta />}</Suspense>
    </FerramentaToolProvider>
  )
}
